/**
 * Firebase client — in-app sign-in (Google today; Apple/Microsoft are console
 * config later). The backend never sees passwords: it only verifies the ID
 * tokens this module hands to api/client.ts.
 *
 * Configure with VITE_FIREBASE_API_KEY / _AUTH_DOMAIN / _PROJECT_ID (these are
 * public client identifiers, safe to ship in the bundle). For local work set
 * VITE_FIREBASE_EMULATOR_HOST (e.g. 127.0.0.1:9099) and the SDK talks to the
 * Firebase Auth emulator instead of Google — no real accounts involved.
 */

import { initializeApp } from "firebase/app";
import {
  ReCaptchaV3Provider,
  getToken as getAppCheckToken,
  initializeAppCheck,
  type AppCheck,
} from "firebase/app-check";
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";

const config = {
  apiKey: (import.meta.env.VITE_FIREBASE_API_KEY as string | undefined) ?? "",
  authDomain: (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined) ?? "",
  projectId: (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) ?? "",
};

export const firebaseConfigured = Boolean(config.apiKey && config.projectId);

let authInstance: Auth | null = null;
let appCheckInstance: AppCheck | null = null;

/**
 * App Check proves a request came from this app rather than from a script
 * replaying a copied ID token. Only active when a reCAPTCHA site key is built
 * in; without one the backend leaves enforcement off too.
 */
const recaptchaSiteKey =
  (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined) ?? "";

export const appCheckConfigured = Boolean(recaptchaSiteKey);

export function firebaseAuth(): Auth | null {
  if (!firebaseConfigured) return null;
  if (!authInstance) {
    const app = initializeApp(config);
    authInstance = getAuth(app);
    void setPersistence(authInstance, browserLocalPersistence).catch(() => {
      // private windows may reject persistence; in-memory sessions still work
    });
    const emulator = import.meta.env.VITE_FIREBASE_EMULATOR_HOST as string | undefined;
    if (emulator) {
      connectAuthEmulator(authInstance, `http://${emulator}`, { disableWarnings: true });
    }
    if (recaptchaSiteKey) {
      try {
        appCheckInstance = initializeAppCheck(app, {
          provider: new ReCaptchaV3Provider(recaptchaSiteKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch {
        // A failure here must not stop sign-in; requests simply arrive
        // unattested and the backend decides what to do about it.
      }
    }
  }
  return authInstance;
}

export async function signInWithGoogle(): Promise<void> {
  const auth = firebaseAuth();
  if (!auth) throw new Error("Firebase is not configured (VITE_FIREBASE_* env vars).");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await signInWithPopup(auth, provider);
}

export async function firebaseSignOut(): Promise<void> {
  const auth = firebaseAuth();
  if (auth) await signOut(auth);
}

/** undefined while Firebase restores the session, then User or null. */
export function subscribeToUser(handler: (user: User | null) => void): () => void {
  const auth = firebaseAuth();
  if (!auth) {
    handler(null);
    return () => {};
  }
  return onAuthStateChanged(auth, handler);
}

/** Attestation for the costly endpoints, or null when not configured. */
export async function currentAppCheckToken(): Promise<string | null> {
  firebaseAuth(); // App Check is set up there; do not rely on call order
  if (!appCheckInstance) return null;
  try {
    return (await getAppCheckToken(appCheckInstance, /* forceRefresh */ false)).token;
  } catch {
    return null;
  }
}

/** Fresh ID token for API calls, or null when signed out / not configured. */
export async function currentIdToken(): Promise<string | null> {
  const auth = firebaseAuth();
  const user = auth?.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}
